import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, UserSearch, Box } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { GuestSearchModal } from '@components/GuestSearchModal';
import eventService from '@services/eventService';
import { useCepSearch } from '@/hooks/useCepSearch';
import { Event, CRMPerson } from '@/types/event';
import { guestToInvitationForm, InvitationRecipientForm } from './invitationUtils';

const emptyRecipient: InvitationRecipientForm = {
  recipient_name: '',
  recipient_document: '',
  recipient_phone: '',
  recipient_email: '',
  ship_addr_street: '',
  ship_addr_num: '',
  ship_addr_compl: '',
  ship_addr_district: '',
  ship_addr_city: '',
  ship_addr_state: '',
  ship_addr_zip: '',
};

// Criação do convite: convidado buscado no CRM (snapshot editável), evento e volume
// (pré-preenchido do padrão do evento, alterável individualmente).
export const InvitationFormView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { searchCep } = useCepSearch();

  const [events, setEvents] = useState<Event[]>([]);
  const [eventId, setEventId] = useState<number | ''>(searchParams.get('event_id') ? Number(searchParams.get('event_id')) : '');
  const [recipient, setRecipient] = useState<InvitationRecipientForm>(emptyRecipient);
  const [observation, setObservation] = useState('');
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestOrigin, setGuestOrigin] = useState<string | null>(null);

  // Volume individual: null = usar o padrão do evento (backend copia).
  const [volume, setVolume] = useState<{ height: string; width: string; length: string; weight: string } | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    eventService.listEvents(1, 100, undefined, true).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const selectedEvent = events.find((ev) => ev.event_id === eventId);
  const hasEventDefaultVolume = Boolean(
    selectedEvent?.default_volume_height && selectedEvent?.default_volume_width
    && selectedEvent?.default_volume_length && selectedEvent?.default_volume_weight,
  );

  // Evento sem volume padrão: o volume individual passa a ser obrigatório — inicializa
  // o formulário de volume (efeito, nunca durante o render).
  useEffect(() => {
    if (selectedEvent && !hasEventDefaultVolume && volume === null) {
      setVolume({ height: '', width: '', length: '', weight: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, hasEventDefaultVolume]);

  const set = (patch: Partial<InvitationRecipientForm>) => setRecipient((r) => ({ ...r, ...patch }));

  const handleGuestSelect = (person: CRMPerson) => {
    setRecipient(guestToInvitationForm(person));
    setGuestOrigin(person.kind === 'commercial_partner' ? 'Personal Organizer do CRM' : 'Cliente do CRM');
  };

  const handleCepBlur = async () => {
    const clean = recipient.ship_addr_zip.replace(/\D/g, '');
    if (clean.length !== 8) return;
    const result = await searchCep(clean);
    if (!result || result.erro) return;
    set({
      ship_addr_street: recipient.ship_addr_street || result.logradouro,
      ship_addr_district: recipient.ship_addr_district || result.bairro,
      ship_addr_city: result.localidade,
      ship_addr_state: result.uf,
    });
  };

  const handleSave = async () => {
    if (!eventId) {
      setError('Selecione o evento do convite.');
      return;
    }
    if (!recipient.recipient_name.trim()) {
      setError('Informe o convidado (busque no CRM ou digite o nome).');
      return;
    }
    if (!volume && !hasEventDefaultVolume) {
      setError('O evento não tem volume padrão cadastrado; informe o volume do convite.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const created = await eventService.createDeliveryOrder({
        delivery_order_type: 1,
        event_id: Number(eventId),
        recipient_name: recipient.recipient_name.trim(),
        recipient_document: recipient.recipient_document || undefined,
        recipient_phone: recipient.recipient_phone || undefined,
        recipient_email: recipient.recipient_email || undefined,
        crm_customer_id: recipient.crm_customer_id,
        crm_comercial_partner_id: recipient.crm_comercial_partner_id,
        ship_addr_street: recipient.ship_addr_street || undefined,
        ship_addr_num: recipient.ship_addr_num || undefined,
        ship_addr_compl: recipient.ship_addr_compl || undefined,
        ship_addr_district: recipient.ship_addr_district || undefined,
        ship_addr_city: recipient.ship_addr_city || undefined,
        ship_addr_state: recipient.ship_addr_state || undefined,
        ship_addr_zip: recipient.ship_addr_zip.replace(/\D/g, '') || undefined,
        observation: observation || undefined,
        volume: volume
          ? {
              height: Number(volume.height) || 0,
              width: Number(volume.width) || 0,
              length: Number(volume.length) || 0,
              diameter: 0,
              weight: Number(volume.weight) || 0,
            }
          : undefined,
      });
      navigate(`/events/invitations/${created.delivery_order_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar convite');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => navigate('/events/invitations')} icon={<ArrowLeft size={16} />} />
        <div>
          <h1 className="text-h1">Novo Convite</h1>
          <p className="text-slate-600">Busque o convidado no CRM; os dados viram um snapshot editável do convite</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <Card>
        <CardHeader title="Evento" />
        <CardContent>
          <select
            className="w-full md:w-96 input-field text-sm"
            value={eventId}
            onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Selecione o evento...</option>
            {events.map((ev) => (
              <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
            ))}
          </select>
          {selectedEvent && !hasEventDefaultVolume && (
            <p className="mt-2 text-xs text-amber-600">
              Este evento não tem volume padrão cadastrado — informe o volume do convite abaixo.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Convidado"
          action={
            <Button size="sm" variant="outline" icon={<UserSearch size={14} />} onClick={() => setGuestModalOpen(true)}>
              Buscar no CRM
            </Button>
          }
          subtitle={guestOrigin ? `Origem: ${guestOrigin} — dados copiados como snapshot, edite se necessário.` : 'Clientes e personal organizers do CRM.'}
        />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome *</label>
              <Input value={recipient.recipient_name} onChange={(e) => set({ recipient_name: e.target.value })} maxLength={150} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">CPF/CNPJ</label>
              <Input value={recipient.recipient_document} onChange={(e) => set({ recipient_document: e.target.value })} maxLength={20} />
              <p className="text-[11px] text-slate-400 mt-1">Obrigatório apenas para emitir a NF de bonificação (envio por transportadora).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Telefone</label>
              <Input value={recipient.recipient_phone} onChange={(e) => set({ recipient_phone: e.target.value })} maxLength={20} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
              <Input value={recipient.recipient_email} onChange={(e) => set({ recipient_email: e.target.value })} maxLength={150} />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">CEP</label>
              <Input value={recipient.ship_addr_zip} onChange={(e) => set({ ship_addr_zip: e.target.value })} onBlur={handleCepBlur} maxLength={9} placeholder="00000-000" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Logradouro</label>
              <Input value={recipient.ship_addr_street} onChange={(e) => set({ ship_addr_street: e.target.value })} maxLength={100} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Número</label>
              <Input value={recipient.ship_addr_num} onChange={(e) => set({ ship_addr_num: e.target.value })} maxLength={10} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Complemento</label>
              <Input value={recipient.ship_addr_compl} onChange={(e) => set({ ship_addr_compl: e.target.value })} maxLength={100} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Bairro</label>
              <Input value={recipient.ship_addr_district} onChange={(e) => set({ ship_addr_district: e.target.value })} maxLength={100} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Cidade</label>
              <Input value={recipient.ship_addr_city} onChange={(e) => set({ ship_addr_city: e.target.value })} maxLength={100} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">UF</label>
              <Input value={recipient.ship_addr_state} onChange={(e) => set({ ship_addr_state: e.target.value.toUpperCase() })} maxLength={2} />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Observação</label>
            <textarea
              className="w-full input-field text-sm min-h-[60px]"
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              maxLength={2000}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Box size={16} className="text-emerald-600" /> Volume do Convite</span>}
          subtitle={hasEventDefaultVolume
            ? `Padrão do evento: ${selectedEvent?.default_volume_height} × ${selectedEvent?.default_volume_width} × ${selectedEvent?.default_volume_length} cm, ${selectedEvent?.default_volume_weight} kg — marque abaixo para usar um volume diferente neste convite.`
            : 'Informe as dimensões e o peso do convite.'}
        />
        <CardContent>
          {hasEventDefaultVolume && (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-3">
              <input
                type="checkbox"
                className="w-4 h-4 text-emerald-600 border-slate-300 rounded"
                checked={volume !== null}
                onChange={(e) => setVolume(e.target.checked ? { height: '', width: '', length: '', weight: '' } : null)}
              />
              Usar volume diferente do padrão do evento
            </label>
          )}
          {volume !== null && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Altura (cm)</label>
                <Input type="number" min={0} step="0.1" value={volume?.height ?? ''} onChange={(e) => setVolume((v) => ({ ...(v ?? { height: '', width: '', length: '', weight: '' }), height: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Largura (cm)</label>
                <Input type="number" min={0} step="0.1" value={volume?.width ?? ''} onChange={(e) => setVolume((v) => ({ ...(v ?? { height: '', width: '', length: '', weight: '' }), width: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comprimento (cm)</label>
                <Input type="number" min={0} step="0.1" value={volume?.length ?? ''} onChange={(e) => setVolume((v) => ({ ...(v ?? { height: '', width: '', length: '', weight: '' }), length: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Peso (kg)</label>
                <Input type="number" min={0} step="0.01" value={volume?.weight ?? ''} onChange={(e) => setVolume((v) => ({ ...(v ?? { height: '', width: '', length: '', weight: '' }), weight: e.target.value }))} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/events/invitations')}>Cancelar</Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={16} />}>
          {saving ? 'Salvando...' : 'Criar Convite'}
        </Button>
      </div>

      <GuestSearchModal isOpen={guestModalOpen} onClose={() => setGuestModalOpen(false)} onSelect={handleGuestSelect} />
    </div>
  );
};
